/**
 * Helpers de débogage pour tester l'application
 * Accessibles via window.biotransac dans la console
 */

export class DebugHelpers {
  /**
   * Affiche toutes les transactions enregistrées
   */
  static showTransactions(): void {
    const transactions = JSON.parse(localStorage.getItem('biotransac_transactions') || '[]');
    console.table(
      transactions.map((t: any) => ({
        ID: t.id.substring(0, 8),
        Type: t.type,
        Montant: `${t.amount.toLocaleString('fr-FR')} FCFA`,
        Client: t.clientName,
        Compte: t.accountNumber,
        Date: new Date(t.createdAt).toLocaleString('fr-FR'),
        Ref: t.reference,
      }))
    );
    console.log(`Total: ${transactions.length} transaction(s)`);
  }

  /**
   * Affiche les comptes disponibles
   */
  static showAccounts(): void {
    const accounts = JSON.parse(localStorage.getItem('biotransac_accounts') || '[]');
    console.table(
      accounts.map((a: any) => ({
        Numéro: a.accountNumber,
        Titulaire: a.primaryHolder.name,
        Type: a.type,
        Statut: a.status,
      }))
    );
  }

  /**
   * Calcule et affiche le solde d'un compte
   */
  static getBalance(accountNumber: string): number {
    const transactions = JSON.parse(localStorage.getItem('biotransac_transactions') || '[]');
    const accountTxns = transactions.filter((t: any) => t.accountNumber === accountNumber);

    const balance = accountTxns.reduce((sum: number, t: any) => {
      return sum + (t.type === 'DEPOSIT' ? t.amount : -t.amount);
    }, 0);

    console.log(`💰 Solde du compte ${accountNumber}: ${balance.toLocaleString('fr-FR')} FCFA`);
    console.log(`📊 Basé sur ${accountTxns.length} transaction(s):`);
    console.table(
      accountTxns.map((t: any) => ({
        Type: t.type === 'DEPOSIT' ? '📥 Versement' : '📤 Retrait',
        Montant: `${t.type === 'DEPOSIT' ? '+' : '-'}${t.amount.toLocaleString('fr-FR')} FCFA`,
        Date: new Date(t.createdAt).toLocaleString('fr-FR'),
      }))
    );

    return balance;
  }

  /**
   * Affiche l'historique complet d'un compte
   */
  static showAccountHistory(accountNumber: string): void {
    const transactions = JSON.parse(localStorage.getItem('biotransac_transactions') || '[]');
    const accountTxns = transactions
      .filter((t: any) => t.accountNumber === accountNumber)
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    console.log(`\n📜 Historique du compte ${accountNumber}\n`);
    console.table(
      accountTxns.map((t: any) => ({
        Date: new Date(t.createdAt).toLocaleString('fr-FR'),
        Type: t.type === 'DEPOSIT' ? '📥 Versement' : '📤 Retrait',
        Montant: `${t.amount.toLocaleString('fr-FR')} FCFA`,
        Client: t.clientName,
        Caissier: t.cashierName,
        Référence: t.reference,
      }))
    );

    this.getBalance(accountNumber);
  }

  /**
   * Affiche les statistiques globales
   */
  static showStats(): void {
    const transactions = JSON.parse(localStorage.getItem('biotransac_transactions') || '[]');
    const deposits = transactions.filter((t: any) => t.type === 'DEPOSIT');
    const withdrawals = transactions.filter((t: any) => t.type === 'WITHDRAWAL');

    const totalDeposits = deposits.reduce((sum: number, t: any) => sum + t.amount, 0);
    const totalWithdrawals = withdrawals.reduce((sum: number, t: any) => sum + t.amount, 0);

    console.log('\n📊 STATISTIQUES GLOBALES\n');
    console.log(`Total transactions: ${transactions.length}`);
    console.log(`  - Versements: ${deposits.length} (${totalDeposits.toLocaleString('fr-FR')} FCFA)`);
    console.log(`  - Retraits: ${withdrawals.length} (${totalWithdrawals.toLocaleString('fr-FR')} FCFA)`);
    console.log(`\nMontant net: ${(totalDeposits - totalWithdrawals).toLocaleString('fr-FR')} FCFA`);

    // Transactions par compte
    const accountGroups = transactions.reduce((groups: any, t: any) => {
      if (!groups[t.accountNumber]) {
        groups[t.accountNumber] = [];
      }
      groups[t.accountNumber].push(t);
      return groups;
    }, {});

    console.log(`\nComptes actifs: ${Object.keys(accountGroups).length}`);
    Object.keys(accountGroups).forEach((accountNumber) => {
      const count = accountGroups[accountNumber].length;
      console.log(`  - ${accountNumber}: ${count} transaction(s)`);
    });
  }

  /**
   * Efface toutes les transactions
   */
  static clearTransactions(): void {
    if (confirm('⚠️ Voulez-vous vraiment effacer toutes les transactions ?')) {
      localStorage.removeItem('biotransac_transactions');
      console.log('✅ Transactions effacées');
      console.log('🔄 Rechargez la page pour voir les changements');
    }
  }

  /**
   * Efface toutes les données
   */
  static clearAll(): void {
    if (confirm('⚠️ Voulez-vous vraiment effacer TOUTES les données ?')) {
      localStorage.clear();
      console.log('✅ Toutes les données effacées');
      console.log('🔄 Rechargez la page');
    }
  }

  /**
   * Affiche l'aide
   */
  static help(): void {
    console.log(`
╔════════════════════════════════════════════════════════════════╗
║                   🔧 BIOTRANSAC DEBUG HELPERS                  ║
╚════════════════════════════════════════════════════════════════╝

Commandes disponibles dans la console:

📊 CONSULTATION:
  biotransac.showTransactions()        - Liste toutes les transactions
  biotransac.showAccounts()            - Liste tous les comptes
  biotransac.getBalance('NUMERO_COMPTE') - Affiche le solde d'un compte
  biotransac.showAccountHistory('NUMERO_COMPTE') - Historique d'un compte
  biotransac.showStats()               - Statistiques globales

🧹 NETTOYAGE:
  biotransac.clearTransactions()       - Effacer les transactions
  biotransac.clearAll()                - Effacer toutes les données

❓ AIDE:
  biotransac.help()                    - Afficher cette aide

💡 EXEMPLES:
  biotransac.showAccounts()  // Liste les comptes disponibles
  biotransac.getBalance('NUMERO_COMPTE')
  biotransac.showStats()
    `);
  }
}

// Exposer dans window pour accès facile dans la console
if (typeof window !== 'undefined') {
  (window as any).biotransac = DebugHelpers;

  // Message de bienvenue
  console.log('%c🏦 BIOTRANSAC V2 - Clean Architecture', 'font-size: 16px; font-weight: bold; color: #0B677E');
  console.log('%cHelper de débogage disponible! Tapez: biotransac.help()', 'color: #666');
}
